import { z } from "zod";
import { DEFAULT_TIMEZONE } from "../constants/constants";
import dayjs from "../../internal-pkgs/dayjs-internal/dayjs";

/**
 * 年度をパースするためのZodスキーマ
 * 文字列または数値を整数の年度に変換する
 */
export const fiscalYearSchema = z.coerce.number().int();

export const getFiscalYear = (date: Date) => {
  // NOTE: TIMEZONEを指定をしなくても、ブラウザのタイムゾーンが使われ、
  // DEFAULT_TIMEZONEと一致する場合は問題なく動作する
  const now = dayjs.tz(date, DEFAULT_TIMEZONE);
  const year = now.year();
  const month = now.month();
  return month < 3 ? year - 1 : year;
};

export const isInFiscalYear = (date: Date, fiscalYear: number) => {
  const now = dayjs.tz(date, DEFAULT_TIMEZONE);
  const year = now.year();
  const month = now.month();
  return month < 3 ? year - 1 === fiscalYear : year === fiscalYear;
};

/**
 * 年度の開始日時を取得する
 * 年度開始日は対象年度の4月1日の00:00:00
 *
 * @param fiscalYear - 対象年度
 * @returns 年度開始日時（Date型）
 *
 * @example
 * // 2026年度の開始日時（2026年4月1日 00:00:00）
 * getFiscalYearStartDate(2026)
 */
export const getFiscalYearStartDate = (fiscalYear: number): Date => {
  // 対象年度の4月1日 00:00:00を計算
  const fiscalYearStart = dayjs.tz(
    `${fiscalYear}-04-01 00:00:00`,
    DEFAULT_TIMEZONE,
  );
  return fiscalYearStart.toDate();
};

/**
 * 年度の終了日時を取得する
 * 年度終了日は対象年度の翌年3月31日の23:59:59
 *
 * @param fiscalYear - 対象年度
 * @returns 年度終了日時（Date型）
 *
 * @example
 * // 2026年度の終了日時（2027年3月31日 23:59:59）
 * getFiscalYearEndDate(2026)
 */
export const getFiscalYearEndDate = (fiscalYear: number): Date => {
  // 対象年度の翌年3月31日 23:59:59を計算
  const fiscalYearEnd = dayjs.tz(
    `${fiscalYear + 1}-03-31 23:59:59`,
    DEFAULT_TIMEZONE,
  );
  return fiscalYearEnd.toDate();
};

/**
 * 年度末年齢を計算する
 * 年度末年齢とは、年度末（翌年3月31日）時点での年齢のこと
 * 年齢計算ニ関スル法律では誕生日前日の午後12時（＝誕生日前日の終了時点）に年齢が変わる
 * つまり、4月1日生まれの人は3月31日に年を取る
 *
 * @param birthDate - 生年月日（Date型またはstring型）
 * @param fiscalYear - 対象年度
 * @returns 年度末年齢（年度末時点での年齢）
 *
 * @example
 * // 1984年3月30日生まれの人の2025年度の年度年齢（2026年3月31日時点で42歳）
 * // 3月30日生まれは3月29日に年を取るため、年度末時点で42歳
 * calculateFiscalYearEndAge(new Date("1984-03-30"), 2025) // => 42
 *
 * @example
 * // 1984年3月31日生まれの人の2025年度の年度年齢（2026年3月31日時点で42歳）
 * // 3月31日生まれは3月30日に年を取るため、年度末時点で42歳
 * calculateFiscalYearEndAge(new Date("1984-03-31"), 2025) // => 42
 *
 * @example
 * // 1984年4月1日生まれの人の2025年度の年度年齢（2026年3月31日時点で42歳）
 * // 4月1日生まれは3月31日に年を取るため、年度末時点で42歳
 * calculateFiscalYearEndAge(new Date("1984-04-01"), 2025) // => 42
 *
 * @example
 * // 1984年4月2日生まれの人の2025年度の年度年齢（2026年3月31日時点で41歳）
 * // 4月2日生まれは4月1日に年を取るため、年度末時点ではまだ41歳
 * calculateFiscalYearEndAge(new Date("1984-04-02"), 2025) // => 41
 */
export const calculateFiscalYearEndAge = (
  birthDate: Date | string,
  fiscalYear: number,
): number => {
  // birthDateを文字列に正規化（タイムゾーンの影響を受けないように）
  const birthDateStr =
    typeof birthDate === "string"
      ? birthDate
      : birthDate.toISOString().split("T")[0];

  // 誕生日
  const birth = dayjs.tz(birthDateStr, DEFAULT_TIMEZONE);
  // 年度末（翌年3月31日）
  const fiscalYearEndDate = dayjs.tz(
    `${fiscalYear + 1}-03-31`,
    DEFAULT_TIMEZONE,
  );

  // 年齢計算ニ関スル法律では誕生日前日の終了時点で年齢が変わる
  // つまり誕生日の1日前を基準として計算する（4月1日生まれ→3月31日が加齢日）
  // 年度末日の翌日（4月1日）と誕生日を比較することで、
  // 誕生日が4月1日の人は年度末時点で既に年を取っている計算になる
  const comparisonDate = fiscalYearEndDate.add(1, "day");

  return comparisonDate.diff(birth, "year");
};

/**
 * 誕生日が属する年度時点での年齢を計算する
 * calculateFiscalYearEndAgeとは異なり、誕生日が属する年度の年齢を計算する
 */
/** @knipignore */
export const calculateAgeAtBirthdayInFiscalYear = (
  birthDate: Date | string,
  fiscalYear: number,
): number => {
  // birthDateを文字列に正規化（タイムゾーンの影響を受けないように）
  const birthDateStr =
    typeof birthDate === "string"
      ? birthDate
      : birthDate.toISOString().split("T")[0];

  const birth = dayjs.tz(birthDateStr, DEFAULT_TIMEZONE);
  const birthMonth = birth.month();
  const birthYear = birth.year();

  const birthdayCalendarYear = birthMonth < 3 ? fiscalYear + 1 : fiscalYear;
  return birthdayCalendarYear - birthYear;
};
